 
import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef, SimpleChanges, inject } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiClientConstant } from '@cureskin/api-client';
import { ConnectionService } from '@services/connection-service';
import { ExperimentService } from '@services/experiment';
import { RegimenService, REGIMEN_FETCH_INCLUDE } from '@services/regimen-service/regimen.service';
import { ReportService } from '@services/report-service/report.service';
import {
  DoctorInfoBottomSheetComponent,
  DoctorInfoBottomSheetData,
  DoctorInfoBottomSheetSource
} from '@shared/bottom-sheet-layouts/doctor-info-bottomsheet/doctor-info-bottomsheet.component';

import { AllocatedRegimen } from '../app-constant-types';
import { AppConfig } from '../app.config';
import {
  SkinData,
  HairData,
  HeroIngredientsResult,
  RootCauses,
  DefaultDrVideo, DefaultDrImages,
} from './report.constants';

export { SkinData, HairData, ConcernsWithCount } from './report.constants';
const MIN_ROOT_CAUSES = 3;

@Component({
  selector: 'report',
  templateUrl: './report.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})

export class ReportComponent {

  @Input('regimen') regimen: any;
  @Input('isTimerPage') isTimerPage: boolean = false;
  @Input('showRegimenToast') showRegimenToast: boolean = false;
  @Output('hideReport') hideReport: EventEmitter<void> = new EventEmitter();
  @Output('showRegimenTimer') showRegimenTimer: EventEmitter<void> = new EventEmitter();

  user: any;
  allocatedDoctor: any;
  skinParametersInfo: SkinData | HairData;
  overallSummary: string;
  reportCode: string;
  patientID: string;
  loading: boolean = false;
  mainConcern: string = '';
  showFirstJourney: boolean = false;
  otherConcerns: string = '';
  severity: any;
  previousInstantCheckup: Array<any> = [];
  showHeroIngredients: boolean = true;
  heroIngredients: any[] = [];
  drVideoExpAssets: { video: string | string[], images: [string, string] } | null = null;
  heroIngredientsCount: number = 3;
  isRebrandedHeroIngredients: boolean = false;
  userFirstName: string = '';
  isEnglishUser: boolean = false;
  onReportPageRoute: boolean = false;
  createdAt: Date = new Date();
  showArrowUIRootCauses: boolean = false;

  experimentService: ExperimentService = inject(ExperimentService);
  rootCauses: RootCauses[];

  private route: ActivatedRoute = inject(ActivatedRoute);
  private regimenService: RegimenService = inject(RegimenService);
  private readonly bottomSheet: MatBottomSheet = inject(MatBottomSheet);

  constructor(
    public readonly conn: ConnectionService,
    private readonly router: Router,
    public readonly appConfig: AppConfig,
    private readonly changeDetection: ChangeDetectorRef,
    private readonly reportService: ReportService,
  ) {
    const state = this.router.currentNavigation()?.extras?.state as { regimen?: any } | undefined;
    if (state?.regimen != null) this.regimen = state.regimen;
  }

  get isHairReport(): boolean {
    return this.regimen?.class === ApiClientConstant.Regimen.Class.HAIR;
  }

  get userLanguage(): string {
    return this.user?.get('languagePreference') || 'en';
  }

  get brandFontClass(): string {
    return this.experimentService.acqRebranding2025 ? 'tw-font-lexend' : 'tw-font-body';
  }

  get showTreatmentPlan(): boolean {
    return !!this.experimentService.treatmentPlanDescriptionExperiment && !this.isHairReport;
  }

  get isFaceUser(): boolean {
    return this.user?.get('PrivateMainConcernClass') === this.appConfig.Shared.Regimen.Class.FACE;
  }

  get resolvedConcernName(): string {
    return this.reportService.resolvePrimaryConcern(
      this.regimen, this.mainConcern, this.user?.get('aiDetections') || [], this.isHairReport, this.userLanguage
    );
  }

  get showRootCausesBanner(): boolean {
    return this.experimentService?.getRootCausesExperiment()?.enable && this.rootCauses?.length === MIN_ROOT_CAUSES;
  }

  async ngOnInit(): Promise<void> {
    this.onReportPageRoute = this.router.url === '/report' || this.router.url.startsWith('/report?');
    this.loading = true;
    this.user = this.conn.getActingUser();
    this.userFirstName = this.user?.get('PatientName')?.split(' ')[0] || '';
    this.isEnglishUser = this.user?.get('languagePreference') === this.appConfig.Shared.Languages.EN;

    if (!this.regimen) {
      this.regimen = this.resolveRegimenFromService();
    }

    // Only fetch experiments on standalone /report route (handles page refresh).
    // When embedded as a child (timer page), experiments are already loaded by the app.
    if (this.onReportPageRoute) {
      await this.experimentService.fetchActiveUserExperiment();
    }
    this.applyReportExperiments();

    // Sometimes allocated doctor obj is not available and template is broken so fetching user details below
    if (!this.user?.get('allocatedDoctor')) await this.conn.getActingUser().fetch();
    this.allocatedDoctor = await this.conn.findUserByObjectId(this.user?.get('allocatedDoctor')?.id);

    this.previousInstantCheckup = await this.conn.fetchInstantCheckup({ userId: this.user.get('username') });
    await this.processCheckupInfo();

    const reportInfo = await this.reportService.getOrCreateReportInfo(this.user);
    this.reportCode = reportInfo.reportCode;
    this.patientID = reportInfo.patientID;
    this.createdAt = reportInfo.createdAt;

    this.showFirstJourney = this.reportService.shouldShowFirstJourney(this.mainConcern);
    this.loading = false;

    if (this.regimen) {
      await this.updateHeroIngredients();
      this.rootCauses = this.regimen?.displayConcern?.rootCause;
    }

    this.changeDetection.detectChanges();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['regimen']?.currentValue && !changes['regimen'].firstChange) {
      this.applyReportExperiments();
      await this.updateHeroIngredients();
      this.changeDetection.detectChanges();
    }
  }

  private applyReportExperiments(): void {
    this.updateDrVideoAssets();
    this.showArrowUIRootCauses = this.experimentService.rootCausesExperiment?.variant?.showArrowUI

    const heroVariant = this.experimentService.getSkinReportHeroIngredientsVariant();
    if (heroVariant?.numberOfIngredients) {
      this.heroIngredientsCount = heroVariant.numberOfIngredients;
      this.isRebrandedHeroIngredients = true;
    }
  }

  private updateDrVideoAssets(): void {
    const exp = this.experimentService.getSkinReportDrVideoExperiment();
    if (!exp?.variant) return;
    const variant = exp.variant;
    const className: 'FACE' | 'HAIR' = this.regimen?.class || 'FACE';
    const language = this.user?.get('languagePreference') || 'en';
    const video = variant?.[className]?.['video']?.[language] || DefaultDrVideo;
    const images = variant?.[className]?.['images']?.[language] || DefaultDrImages;
    this.drVideoExpAssets = { video, images };
  }

  private async processCheckupInfo(): Promise<void> {
    const concernResult = this.buildConcernData();
    await this.fetchSkinReportData(concernResult);
    this.resolveConcernsAndSeverity();
  }

  private buildConcernData() {
    this.previousInstantCheckup = this.reportService.filterCheckups(
      this.previousInstantCheckup, this.isHairReport
    );

    const { mergedMap } = this.reportService.mergeDetectedConcerns(
      this.previousInstantCheckup
    );

    const concernResult = this.reportService.buildConcernCounts(mergedMap, this.regimen?.displayConcern?.name);
    if (!this.regimen?.displayConcern?.name && !this.mainConcern) {
      this.mainConcern = concernResult.mainConcern;
    }
    return concernResult;
  }

  private async fetchSkinReportData(concernResult: { mainConcernKey: string, payload: any }): Promise<void> {
    const skinReport = await this.conn.getSkinReportInfo(concernResult.mainConcernKey, concernResult.payload);
    this.showRegimenTimer.emit();

    this.skinParametersInfo = this.isHairReport
      ? this.reportService.parseHairReportParams(this.regimen)
      : this.reportService.parseSkinReportParams(this.regimen, skinReport);

    if (this.regimen?.displayConcern?.name) {
      this.mainConcern = this.regimen.displayConcern.name;
    }

    const summaryConcern = this.regimen?.displayConcern?.treatmentConcerns?.[0] ?? this.mainConcern;
    this.overallSummary = await this.reportService.resolveOverallSummary(
      this.regimen, this.isHairReport, summaryConcern, concernResult.payload, this.userLanguage
    );
  }

  private resolveConcernsAndSeverity(): void {
    const detectedConcerns = this.user?.get('aiDetections') || [];
    const firstFrontFaceCheckup = this.reportService.getFirstFrontFaceCheckup(
      this.previousInstantCheckup, this.isHairReport
    );

    this.otherConcerns = this.reportService.resolveOtherConcerns(
      this.regimen, this.mainConcern, detectedConcerns, firstFrontFaceCheckup, this.isHairReport, this.userLanguage
    );

    this.severity = this.reportService.resolveSeverity(this.regimen, firstFrontFaceCheckup, this.mainConcern, this.userLanguage);
  }

  back(): void {
    this.router.navigate(['/user'], { queryParams: { tab: 'home' } });
  }

  openDoctorBottomSheet(): void {
    if (!this.allocatedDoctor) return;
    this.bottomSheet.open(DoctorInfoBottomSheetComponent, {
      data: { doctor: this.allocatedDoctor, source: DoctorInfoBottomSheetSource.REPORT } as DoctorInfoBottomSheetData,
    });
  }

  private resolveRegimenFromService(): any {
    const regimenClass = this.route.snapshot.queryParamMap.get('class');
    if (!regimenClass) return null;
    const regimens = this.regimenService.regimens();
    return regimens?.find((r: any) => r?.class === regimenClass) ?? null;
  }

  private async updateHeroIngredients(): Promise<void> {
    let result = this.reportService.resolveHeroIngredients(
      this.regimen, this.isRebrandedHeroIngredients, this.heroIngredientsCount, this.isHairReport
    );

    if (!result.ingredients.length && this.regimen?.variants?.length) {
      result = await this.refetchHeroIngredients();
    }

    this.heroIngredients = result.ingredients;
    this.showHeroIngredients = result.show;
  }

  private async refetchHeroIngredients(): Promise<HeroIngredientsResult> {
    const regimens = await this.conn.fetchRegimens(undefined, true, true, REGIMEN_FETCH_INCLUDE);
    const freshRegimen = regimens?.find((r: AllocatedRegimen) => r?.regimenId === this.regimen.regimenId);

    if (!freshRegimen) return { ingredients: [], show: false };

    this.regimen = freshRegimen;
    return this.reportService.resolveHeroIngredients(
      this.regimen, this.isRebrandedHeroIngredients, this.heroIngredientsCount, this.isHairReport
    );
  }
}
